import React, { useState } from 'react'
import styled, { css } from 'styled-components'
import { useTranslation } from 'react-i18next'

// Components
import Button, { ButtonVariant } from './Button'
import Input from './formControl/Input'
import Text from './Text'
import Dropdown from './formControl/Dropdown'

// Constants
import {
  ALL_FILTER,
  LEAD_FILTER,
  ADMIN_FILTER,
  SUGGESTED_FILTER,
} from '../hooks/useTeamFilters'

// Types
import { ICompanyMember } from './../common/models/Company'

export type IFilterType = 'team' | 'other'

export interface IFilter {
  id?: string
  name: string
  icon?: string
  members?: ICompanyMember[]
  onlyForAdmin?: boolean
  type?: IFilterType
  separator?: boolean
}

interface IProps {
  className?: string
  isAdmin: boolean
  forceMobile?: boolean
  title?: string
  staffFilters?: IFilter[]
  selectedFilter?: IFilter
  searchTerm?: string
  setSelectedFilter: React.Dispatch<React.SetStateAction<IFilter | undefined>>
  setSearchTerm?: React.Dispatch<React.SetStateAction<string>>
}

export const getImage = (team: string) => {
  switch (team) {
    case LEAD_FILTER:
      return '🚩'
    case ADMIN_FILTER:
      return '👑'
    case SUGGESTED_FILTER:
      return '👀'
    case ALL_FILTER:
      return '🏢'
    default:
      return '👻'
  }
}

interface ISearchInputProps {
  className?: string
  setSearchTerm?: React.Dispatch<React.SetStateAction<string>>
  searchTerm?: string
  label?: string
  placeholder?: string
  coloredBackground?: boolean
}

export const SearchInput: React.FC<ISearchInputProps> = ({
  setSearchTerm,
  coloredBackground,
  searchTerm,
  placeholder,
  label,
  className,
}) => {
  const { t } = useTranslation()
  return setSearchTerm ? (
    <Form
      id="searchInput"
      className={className}
      onSubmit={(e) => e.preventDefault()}
    >
      <Input
        label={label}
        coloredBackground={coloredBackground}
        value={searchTerm}
        onChange={(event) => {
          setSearchTerm(event.currentTarget.value)
        }}
        placeholder={placeholder || t('general.forms.placeholderSearch')}
        withSearch={true}
      />
    </Form>
  ) : null
}

const TeamFilters: React.FC<IProps> = ({
  className,
  isAdmin,
  title,
  forceMobile,
  staffFilters,
  selectedFilter,
  searchTerm,
  setSelectedFilter,
  setSearchTerm,
}) => {
  const { t } = useTranslation()
  const [openMobileFilters, setOpenMobileFilters] = useState<boolean>(
    forceMobile ? false : true
  )

  const renderFilterCards = (filters?: IFilter[]) => {
    if (!filters) return

    return (
      <StyledDropdown
        value={selectedFilter?.name}
        onChange={(e) => {
          e.persist()
          const chosenFilter = filters.find(
            (filter) => filter.name === e.target.value
          )
          setSelectedFilter(chosenFilter)
        }}
      >
        {filters
          .filter((filter) => (filter.onlyForAdmin ? isAdmin : filter))
          .map((filter, index) => (
            <React.Fragment key={`filter-${filter.name}-${index}`}>
              <option value={filter.name}>
                {`${filter.icon ? `${filter.icon} ` : ''}${t(filter.name)}`}
              </option>
              {filter.separator && <option disabled>───────────────</option>}
            </React.Fragment>
          ))}
      </StyledDropdown>
    )
  }

  return (
    <Container className={className}>
      {setSearchTerm && (
        <SearchInput setSearchTerm={setSearchTerm} searchTerm={searchTerm} />
      )}
      {staffFilters && (
        <>
          <StyledButton
            color="primary"
            show={forceMobile}
            variant={ButtonVariant.Text}
            icon={openMobileFilters ? 'ChevronDown' : 'ChevronRight'}
            caption={
              openMobileFilters
                ? t('general.forms.hideFilters')
                : t('general.forms.showFilters')
            }
            onClick={() => setOpenMobileFilters(!openMobileFilters)}
          />
          <FilterContainer open={openMobileFilters} show={!forceMobile}>
            {title && (
              <Text marginBottom={8} textStyle="labelTinyCaps">
                {title}
              </Text>
            )}
            {staffFilters &&
              staffFilters.length > 0 &&
              renderFilterCards(staffFilters)}
          </FilterContainer>
        </>
      )}
    </Container>
  )
}

const Container = styled.div``

const Form = styled.form``

const StyledDropdown = styled(Dropdown)`
  margin-bottom: 12px;
`

const FilterContainer = styled.div<{ open: boolean; show?: boolean }>`
  margin: 24px 0;
  display: ${({ open }) => (open ? 'block' : 'none')};

  ${({ show }) =>
    show &&
    css`
      display: block;
    `}
`

const StyledButton = styled(Button)<{ show?: boolean }>`
  padding-left: 0;
  margin-bottom: 24px;

  ${({ show }) =>
    !show &&
    css`
      display: none;
    `}
`

export default TeamFilters
